<script lang="ts">
	import { auth, AuthError, type AuthUser } from '../lib/auth'
	import { ENVIRONMENT } from '../lib/config'

	let { onlogin }: { onlogin: (u: AuthUser) => void } = $props()

	let email = $state('')
	let password = $state('')
	let show = $state(false)
	let busy = $state(false)
	let error = $state('')

	async function submit(e: SubmitEvent) {
		e.preventDefault()
		if (busy) return
		if (!/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(email.trim())) {
			error = 'Enter a valid work email address.'
			return
		}
		if (!password) {
			error = 'Enter your password.'
			return
		}
		busy = true
		error = ''
		try {
			onlogin(await auth.login(email.trim(), password))
		} catch (err) {
			error = err instanceof AuthError ? err.message : 'The server is unreachable. Try again.'
		} finally {
			busy = false
		}
	}
</script>

<div class="page">
	<section class="hero">
		<div class="brand">
			<span class="mark"></span>
			<span class="name">REELDESK</span>
			<span class="ver">v{__VERSION__} · {ENVIRONMENT}</span>
		</div>

		<div class="pitch">
			<div class="kicker">INTERNAL TOOLING · ACCESS RESTRICTED</div>
			<div class="headline">Launch any slot,<br />on any wallet,<br />in one click.</div>
			<div class="sub">
				Session config, demo balances and locale switching for the whole certified catalog. Sign
				in with your engineering account.
			</div>
		</div>

		<div class="status">
			<span class="dot"></span>
			<span>all game servers operational · rgs 12ms</span>
		</div>

		<div class="glow"></div>
	</section>

	<section class="pane">
		<form class="form" onsubmit={submit}>
			<div class="head">
				<div class="title">Sign in</div>
				<div class="hint">One account for the launcher and the back office</div>
			</div>

			<label class="field">
				<span>work email</span>
				<input
					type="text"
					bind:value={email}
					placeholder="ivan@reeldesk.dev"
					autocomplete="username"
				/>
			</label>

			<label class="field">
				<span>password</span>
				<div class="passwrap">
					<input
						type={show ? 'text' : 'password'}
						bind:value={password}
						placeholder="••••••••••"
						autocomplete="current-password"
					/>
					<button type="button" class="peek" onclick={() => (show = !show)}>
						{show ? 'HIDE' : 'SHOW'}
					</button>
				</div>
			</label>

			{#if error}
				<div class="error">{error}</div>
			{/if}

			<button type="submit" class="go" disabled={busy}>
				{busy ? 'SIGNING IN…' : 'SIGN IN'}
			</button>

			<div class="note">
				All sessions are logged. Demo wallets only — no real-money accounts are reachable from
				this environment.
			</div>
		</form>
	</section>
</div>

<style>
	.page {
		min-height: 100vh;
		display: grid;
		grid-template-columns: minmax(0, 1fr) 520px;
	}
	@media (max-width: 900px) {
		.page {
			grid-template-columns: 1fr;
		}
		.hero {
			display: none;
		}
	}

	.hero {
		position: relative;
		overflow: hidden;
		background: var(--panel);
		border-right: 1px solid var(--line);
		display: flex;
		flex-direction: column;
		padding: 44px 48px;
	}
	.brand {
		display: flex;
		align-items: center;
		gap: 10px;
	}
	.mark {
		width: 10px;
		height: 10px;
		background: var(--accent);
		border-radius: 2px;
	}
	.name {
		font-size: 13px;
		font-weight: 700;
		letter-spacing: 0.06em;
	}
	.ver {
		margin-left: auto;
		font-size: 10px;
		color: var(--ghost);
		letter-spacing: 0.08em;
	}

	.pitch {
		margin-top: auto;
		display: flex;
		flex-direction: column;
		gap: 22px;
		max-width: 520px;
	}
	.kicker {
		font-size: 10px;
		letter-spacing: 0.18em;
		color: var(--accent);
	}
	.headline {
		font-size: 38px;
		font-weight: 700;
		line-height: 1.18;
		letter-spacing: -0.02em;
		text-wrap: pretty;
	}
	.sub {
		font-size: 13px;
		line-height: 1.8;
		color: var(--muted);
		text-wrap: pretty;
	}

	.status {
		margin-top: auto;
		padding-top: 40px;
		display: flex;
		align-items: center;
		gap: 10px;
		font-size: 11px;
		color: var(--dim);
	}
	.dot {
		width: 7px;
		height: 7px;
		border-radius: 50%;
		background: var(--ok);
	}
	.glow {
		position: absolute;
		right: -140px;
		bottom: -140px;
		width: 460px;
		height: 460px;
		border-radius: 50%;
		background: radial-gradient(circle, rgba(91, 140, 255, 0.16), transparent 68%);
		pointer-events: none;
	}

	.pane {
		display: flex;
		flex-direction: column;
		justify-content: center;
		padding: 48px 56px;
	}
	.form {
		display: flex;
		flex-direction: column;
		gap: 22px;
	}
	.head {
		display: flex;
		flex-direction: column;
		gap: 7px;
	}
	.title {
		font-size: 19px;
		font-weight: 700;
	}
	.hint {
		font-size: 11px;
		color: var(--dim);
	}

	.field {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}
	.field > span {
		font-size: 11px;
		color: var(--muted);
	}
	.field input {
		width: 100%;
		min-width: 0;
		box-sizing: border-box;
		background: var(--bg);
		border: 1px solid var(--line-strong);
		border-radius: var(--radius);
		padding: 12px 13px;
		color: var(--text);
		font-size: 13px;
	}
	.field input:focus {
		outline: none;
		border-color: var(--accent);
	}

	.passwrap {
		position: relative;
		display: flex;
	}
	.passwrap input {
		flex: 1;
		padding-right: 66px;
	}
	.peek {
		position: absolute;
		right: 6px;
		top: 6px;
		bottom: 6px;
		background: var(--btn);
		border: 1px solid var(--line-strong);
		border-radius: 4px;
		color: var(--muted);
		padding: 0 9px;
		font-size: 10px;
		letter-spacing: 0.06em;
		cursor: pointer;
	}
	.peek:hover {
		border-color: var(--accent);
		color: var(--text);
	}

	.error {
		border: 1px solid var(--danger-line);
		background: var(--danger-bg);
		border-radius: var(--radius);
		padding: 11px 12px;
		font-size: 11px;
		line-height: 1.6;
		color: var(--danger-text);
	}

	.go {
		background: var(--accent);
		border: none;
		border-radius: var(--radius);
		color: var(--on-accent);
		padding: 14px 0;
		font-size: 12px;
		font-weight: 700;
		letter-spacing: 0.12em;
		cursor: pointer;
	}
	.go:hover {
		background: var(--accent-hover);
	}
	.go:disabled {
		opacity: 0.7;
		cursor: default;
	}

	.note {
		font-size: 11px;
		line-height: 1.8;
		color: var(--ghost);
		text-wrap: pretty;
	}
</style>
